import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { TextField, Box, Chip } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { ToolTip } from "../../ToolTip/ToolTip";
import { naicsCodeContent } from "../../ToolTip/ToolTipData";
import BenevvDropDown from "components/forms/BenevvDropDown/BenevvDropDown";
import CreateOrganizationcss from "../../CreateOrgMain/CreateOrganization.module.css";
import {
  naicsData,
  orgIndustryBrands,
  orgIndustryNonProfits,
  orgSize,
  orgTypeData,
} from "../../DropDownData/DropDownData";
import {
  StyleAutoComplete,
  StyleNaicsLabel,
  StyleDatePicker,
} from "./OrgCharacteristics.styles";
interface OrgCharacteristicsProps {
  setFoundationDate: (field: string) => void;
  setOrgType: (field: string) => void;
  setOrgIndustry: (field: string) => void;
  setOrgSize: (field: string) => void;
  orgType: string;
  setNaicsCode: (field: any) => void;
}

const StackedField = ({ label, children, fullWidth = false }: { label: React.ReactNode; children: React.ReactNode; fullWidth?: boolean }) => (
  <div style={{ display: 'flex', flexDirection: 'column', flex: fullWidth ? '1 1 100%' : '1 1 46%', minWidth: fullWidth ? '100%' : 260, maxWidth: fullWidth ? '100%' : '46%', marginBottom: '1.25rem' }}>
    <label style={{ color: '#fe2f67', fontSize: '1.05rem', marginBottom: 6, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}>{label}</label>
    {children}
  </div>
);

export function OrgCharacteristics({
  setFoundationDate,
  setOrgIndustry,
  setOrgSize,
  setOrgType,
  orgType,
  setNaicsCode,
}: OrgCharacteristicsProps) {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<any[]>(naicsData.slice(0, 10));
  const [selected, setSelected] = useState<any[]>([]);
  const [limitMsg, setLimitMsg] = useState("");
  const [foundationDateLocal, setFoundationDateLocal] = useState<Dayjs | null>(
    null
  );
  const handleNaicsChange = (_e: any, value: unknown) => {
    const newValue = value as any[];
    if (newValue.length > 5) {
      setLimitMsg("You can select up to 5 codes");
      return;
    }
    setLimitMsg("");
    setSelected(newValue);
  };
  useEffect(() => {
    const id = setTimeout(() => {
      if (inputValue.trim() === "") {
        setOptions(naicsData.slice(0, 10));
        return;
      }
      const txt = inputValue.toLowerCase();
      const filtered = naicsData
        .filter(
          (o) => o.code.includes(txt) || o.title.toLowerCase().includes(txt)
        )
        .slice(0, 10); // show only 10
      setOptions(filtered);
    }, 500); // 0.5 s debounce

    return () => clearTimeout(id);
  }, [inputValue]);
  useEffect(() => {
    setNaicsCode(selected.map((o) => o.code).join(","));
  }, [selected, setNaicsCode]);

  return (
    <>
      <h2 className={CreateOrganizationcss.sectionHeader}>
        3. Organization Characteristic
      </h2>
      {/* Row: Founding Date + NAICS Code */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', marginBottom: '1.25rem', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', flex: '1 1 260px', minWidth: 200, maxWidth: '100%', justifyContent: 'flex-end' }}>
          <label style={{ color: '#fe2f67', fontSize: '1.05rem', marginBottom: '0.375rem', whiteSpace: 'nowrap' }}>Founding Date *</label>
          <div style={{ width: '100%' }}>
            <StyleDatePicker style={{ width: '100%' }}>
              <ThemeProvider theme={createTheme({ palette: { primary: { main: "#fe2f67" } } })}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={undefined}>
                  <DatePicker
                    label=""
                    disableFuture
                    value={foundationDateLocal}
                    onChange={(newValue) => {
                      setFoundationDateLocal(newValue);
                      const formatted = newValue ? newValue.format("YYYY-MM-DD") : "";
                      setFoundationDate(formatted);
                    }}
                    slotProps={{
                      textField: {
                        required: true,
                        sx: {
                          width: "100%",
                          minHeight: "3rem",
                          height: "3rem",
                          maxHeight: "3rem",
                          '& .MuiInputBase-root': {
                            minHeight: '3rem',
                            height: '3rem',
                            maxHeight: '3rem',
                            borderRadius: '5px',
                            fontSize: '1rem',
                            background: '#fff',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#d9d9d9',
                          },
                        },
                        placeholder: "Select date"
                      },
                    }}
                  />
                </LocalizationProvider>
              </ThemeProvider>
            </StyleDatePicker>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', flex: '2 1 400px', minWidth: 320, maxWidth: '100%' }}>
          <label style={{ color: '#fe2f67', fontSize: '1.05rem', marginBottom: '0.375rem', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}>
            NAICS Code <ToolTip toolTipData={naicsCodeContent} />
          </label>
          <StyleAutoComplete
            multiple
            options={options}
            inputValue={inputValue}
            value={selected}
            filterOptions={(x) => x}
            onInputChange={(_, v) => setInputValue(v)}
            onChange={handleNaicsChange}
            getOptionLabel={(o: any) => o.code}
            renderOption={(props, option: any) => (
              <Box {...props} key={option.code} component="li">
                <p style={{ marginRight: "3em" }}>{option.code}</p>
                <p>{option.title}</p>
              </Box>
            )}
            renderTags={(
              tagValue: any[],
              getTagProps
            ) =>
              tagValue.map((option: any, index: number) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.code}
                  label={option.code}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Type to search NAICS"
                helperText={limitMsg ? "You can select up to 5 codes" : undefined}
                error={Boolean(limitMsg)}
              />
            )}
            slotProps={{
              popper: { className: 'naics-popper' }
            }}
          />
        </div>
      </div>
      {/* Fila triple: Type, Industry, Size */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem', marginBottom: '1.25rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', flex: '1 1 0', minWidth: 200, maxWidth: '32%' }}>
          <label style={{ color: '#fe2f67', fontSize: '1.05rem', marginBottom: 6, whiteSpace: 'nowrap' }}>Organization Type</label>
          <BenevvDropDown
            styleDropDown={{ width: "100%", minHeight: "3rem", height: "3rem", maxHeight: "3rem", boxSizing: "border-box", marginBottom: "10px" }}
            label=""
            placeholder="Select organization type"
            setTextField={setOrgType}
            values={orgTypeData}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', flex: '1 1 0', minWidth: 200, maxWidth: '32%' }}>
          <label style={{ color: '#fe2f67', fontSize: '1.05rem', marginBottom: 6, whiteSpace: 'nowrap' }}>Organization Industry</label>
          <BenevvDropDown
            styleDropDown={{ width: "100%", minHeight: "3rem", height: "3rem", maxHeight: "3rem", boxSizing: "border-box", marginBottom: "10px" }}
            label=""
            placeholder="Select organization industry"
            setTextField={setOrgIndustry}
            values={orgIndustryBrands}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', flex: '1 1 0', minWidth: 200, maxWidth: '32%' }}>
          <label style={{ color: '#fe2f67', fontSize: '1.05rem', marginBottom: 6, whiteSpace: 'nowrap' }}>Organization Size</label>
          <BenevvDropDown
            styleDropDown={{ width: "100%", minHeight: "3rem", height: "3rem", maxHeight: "3rem", boxSizing: "border-box", marginBottom: "10px" }}
            label=""
            placeholder="Select organization size"
            setTextField={setOrgSize}
            values={orgSize}
          />
        </div>
      </div>
    </>
  );
}
